import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [newPage, setNewPage] = useState({ slug: "", title: "", content: "", menu_location: "none" });

  const load = () => api.get("/admin/pages").then((r) => {
    setPages(r.data);
    setActive((a) => a || (r.data[0]?.slug ?? ""));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (slug, k, v) => setPages((ps) => ps.map((p) => p.slug === slug ? { ...p, [k]: v } : p));

  const save = async (p) => {
    try {
      await api.put(`/admin/pages/${p.slug}`, { title: p.title, content: p.content, seo_title: p.seo_title || "", seo_description: p.seo_description || "", menu_location: p.menu_location || "none" });
      toast.success("Page saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const create = async () => {
    if (!newPage.slug || !newPage.title) { toast.error("Slug and title are required"); return; }
    try {
      const { data } = await api.post("/admin/pages", newPage);
      toast.success("Page created");
      setAddOpen(false); setNewPage({ slug: "", title: "", content: "", menu_location: "none" });
      await load(); setActive(data.slug);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete the "${slug}" page? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pages/${slug}`);
      toast.success("Page deleted");
      setActive("");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Pages / CMS</h1>
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => setAddOpen(true)} data-testid="add-page-btn"><Plus className="mr-1.5 h-4 w-4" />Add Page</Button>
      </div>

      {pages.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">No pages yet. Click "Add Page" to create one.</div>
      ) : (
        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="mb-4 flex-wrap">
            {pages.map((p) => <TabsTrigger key={p.slug} value={p.slug} data-testid={`page-tab-${p.slug}`}>{p.title}</TabsTrigger>)}
          </TabsList>
          {pages.map((p) => (
            <TabsContent key={p.slug} value={p.slug}>
              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500">/page/{p.slug}</span>
                  <a href={`/page/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-800 hover:underline">Preview <ExternalLink className="h-3 w-3" /></a>
                </div>
                <div><Label>Title</Label><Input value={p.title} onChange={(e) => set(p.slug, "title", e.target.value)} /></div>
                <div><Label>Content</Label><div className="mt-1"><RichTextEditor value={p.content} onChange={(v) => set(p.slug, "content", v)} testId={`page-content-${p.slug}`} /></div></div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><Label>SEO Title</Label><Input value={p.seo_title || ""} onChange={(e) => set(p.slug, "seo_title", e.target.value)} /></div>
                  <div><Label>SEO Description</Label><Input value={p.seo_description || ""} onChange={(e) => set(p.slug, "seo_description", e.target.value)} /></div>
                </div>
                <div>
                  <Label>Display in</Label>
                  <select
                    value={p.menu_location || "none"}
                    onChange={(e) => set(p.slug, "menu_location", e.target.value)}
                    data-testid={`page-menu-location-${p.slug}`}
                    className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                    <option value="none">Hidden (accessible by direct link only)</option>
                    <option value="main">Main Menu (header)</option>
                    <option value="footer">Footer</option>
                    <option value="both">Both Header and Footer</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => save(p)} data-testid={`save-page-${p.slug}`}>Save Page</Button>
                  <Button variant="ghost" className="text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => del(p.slug)} data-testid={`delete-page-${p.slug}`}><Trash2 className="mr-1.5 h-4 w-4" />Delete</Button>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Page</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Page Title</Label><Input value={newPage.title} onChange={(e) => setNewPage((n) => ({ ...n, title: e.target.value }))} placeholder="e.g. FAQ" data-testid="new-page-title" /></div>
            <div><Label>Slug (URL)</Label><Input value={newPage.slug} onChange={(e) => setNewPage((n) => ({ ...n, slug: e.target.value }))} placeholder="e.g. faq → /page/faq" data-testid="new-page-slug" /></div>
            <div><Label>Content</Label><div className="mt-1"><RichTextEditor value={newPage.content} onChange={(v) => setNewPage((n) => ({ ...n, content: v }))} testId="new-page-content" /></div></div>
            <div>
              <Label>Display in</Label>
              <select value={newPage.menu_location} onChange={(e) => setNewPage((n) => ({ ...n, menu_location: e.target.value }))}
                data-testid="new-page-menu-location" className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="none">Hidden (direct link only)</option>
                <option value="main">Main Menu (header)</option>
                <option value="footer">Footer</option>
                <option value="both">Both Header and Footer</option>
              </select>
            </div>
          </div>
          <DialogFooter><Button className="bg-blue-800 hover:bg-blue-900" onClick={create} data-testid="create-page-btn">Create Page</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
